// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Loader,
  HistoricalBalancesChart,
} from '@shared/advisory/aggregation/components';
import {
  DonutChartContainer,
  IconButton,
  OptionsSelector,
  PeriodPicker,
  SectionContainer,
  Translation,
} from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { usePositions } from './use-positions';
import { PositionsTable } from './positions-table';
import { ManageTagsModal } from '@shared/tag-management/components/tags-manager';
import { Tooltip } from '@mui/material';
import { PositionsViewToggle } from '@shared/advisory/aggregation/components';

export const Positions = defineComponent({
  displayName: 'Positions',
  useSetup: usePositions,
  View: ({
    holdings,
    breakdowns,
    hasHoldings,
    status,
    historicalBalances,
    onDownload,
    periodPicker,
    currency,
    hasHistoricalBalances,
    institutionId,
    onRefresh,
    viewMode,
    setViewMode,
    onTransfer,
    onBuy,
    onSell,
    portfolioFunds,
    canUseTrader,
    ...props
  }) => (
    <div className="mt-6 space-y-10">
      <Loader
        status={status}
        shouldDisplayEmptyState={!hasHoldings}
        institution={institutionId}
        onRefresh={onRefresh}
      >
        <div className="flex flex-col items-center justify-center gap-5 sm:flex-row sm:justify-start">
          <PeriodPicker
            onValueChange={periodPicker.onPeriodChange}
            availableOptions={['YTD', '1M', '3M', '6M', '1Y']}
            defaultValue={periodPicker.defaultHist}
          />
        </div>

        <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] xl:items-stretch">
          {hasHistoricalBalances && (
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
              <HistoricalBalancesChart
                currency={currency}
                data={historicalBalances}
                fixedSerie="investment"
              />
            </div>
          )}

          <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
            <DonutChartContainer
              exposure={breakdowns.map(({ name }) => name)}
              weight={breakdowns.map(({ weight }) => weight)}
              name={<Translation i18nKey="glossary.positions" ns="glossary" />}
              sort={false}
              compactLayout
              divider={false}
            />
          </div>
        </div>

        <SectionContainer
          title={
            <Translation i18nKey="glossary.investment-positions" ns="glossary" />
          }
          actions={
            <div className="flex items-center space-x-4">
              <PositionsViewToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
              <div className="w-52">
                <OptionsSelector
                  optionsAvailable={props.optionsAvailable}
                  onChangeValueSelected={props.onChangeTag}
                  optionSelected={props.selectTag}
                  defaultText={props.defaultText}
                  selectType="white"
                  anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom',
                  }}
                  disabled={props.optionsAvailable.length === 0}
                />
              </div>
              <Tooltip title="Manage Tags">
                <IconButton
                  icon="Plus"
                  onClick={props.handleOpenModal}
                  data-testid="manage-tags-test-id"
                  disabled={holdings.length === 0}
                />
              </Tooltip>
            </div>
          }
        >
          <PositionsTable
            data={holdings}
            onDownload={onDownload}
            isMobile={false}
            onLoad={props.onLoad}
            onComparison={props.onComparison}
            viewMode={viewMode}
            onTransfer={onTransfer}
            onBuy={onBuy}
            onSell={onSell}
            portfolioFunds={portfolioFunds}
          />
        </SectionContainer>

        <ManageTagsModal
          onClose={props.handleCloseModal}
          openModal={props.openTagsModal}
          tags={props.tags}
          tickerTags={props.tickerTags}
          onCreateTag={props.onCreateTag}
          onCreateTickerTag={props.onCreateTickerTag}
          suggestions={props.suggestions}
          dataSource={holdings as any[]}
        />
      </Loader>
    </div>
  ),
});
